import { useRegisterSW } from "virtual:pwa-register/react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigationType } from "react-router";
import { Button } from "@/components/ui/button";

const HOUR = 60 * 60 * 1000;

/**
 * Registro do service worker e troca de versão (M1, itens 17 e 18; decisão P2).
 * - Procura versão nova a cada hora e sempre que o app volta a ficar visível (no celular, o app fica dias em
 *   segundo plano sem ser "aberto de novo").
 * - Versão nova antes de qualquer navegação ou digitação desde a abertura: aplica sem perguntar (nada a perder).
 * - Depois disso: aviso fixo "Nova versão disponível" com "Atualizar". Não fecha nem recarrega sozinho, para não
 *   descartar um formulário aberto.
 * O `workbox-window` é importado sob demanda pelo plugin, fora do JS inicial.
 */
export function UpdatePrompt() {
  const interacted = useUserActivity();
  const [registration, setRegistration] = useState<ServiceWorkerRegistration>();
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({ onRegisteredSW: (_url, r) => setRegistration(r) });

  useEffect(() => {
    if (!registration) return;
    const check = () => void registration.update().catch(() => undefined);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    const timer = setInterval(check, HOUR);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [registration]);

  const autoApplied = useRef(false);
  useEffect(() => {
    if (needRefresh && !interacted && !autoApplied.current) {
      autoApplied.current = true;
      void updateServiceWorker(true);
    }
  }, [needRefresh, interacted, updateServiceWorker]);

  if (!needRefresh || !interacted) return null;
  return (
    <div
      role="status"
      aria-label="Nova versão disponível"
      className="fixed inset-x-4 bottom-[calc(max(env(safe-area-inset-bottom),12px)+84px)] z-50 mx-auto flex max-w-md items-center justify-between gap-3 rounded-card border border-border bg-surface px-4 py-3 text-sm text-ink shadow-float md:right-6 md:bottom-6 md:left-auto"
    >
      <span className="font-medium">Nova versão disponível</span>
      <Button onClick={() => void updateServiceWorker(true)}>Atualizar</Button>
    </div>
  );
}

/** Verdadeiro depois da primeira navegação do usuário (não conta redirecionamento) ou da primeira digitação. */
function useUserActivity() {
  const [active, setActive] = useState(false);
  const location = useLocation();
  const navigationType = useNavigationType();
  const first = useRef(location.key);

  useEffect(() => {
    if (location.key !== first.current && navigationType !== "REPLACE") setActive(true);
  }, [location.key, navigationType]);

  useEffect(() => {
    if (active) return;
    const onInput = () => setActive(true);
    document.addEventListener("input", onInput, true);
    return () => document.removeEventListener("input", onInput, true);
  }, [active]);

  return active;
}
